type Timeout = ReturnType<typeof setTimeout>;

/**
 * Returns a debounced function that will make sure the given function is not invoked too often.
 * If leading is false, will only be invoked after the timeout has expired since last invocation.
 * If leading is true, will be invoked at the leading edge of the timeout.
 *
 * @param fn The function to debounce
 * @param delay The delay in milliseconds before invoking the function
 * @param [leading=false] A flag that determines whether the function should be invoked at the
 *   leading or trailing edge of the timeout.
 * @returns A new debounced function.
 *
 * @example
 * function expensiveOperation(value: number) {
 *   console.log(`expensive operation with ${value}`);
 * }
 * const debounced = debounce(expensiveOperation, 1000);
 * debounced(1); // outputs nothing
 * debounced(2); // outputs nothing
 * debounced(3); // outputs: invoke expensive operation with 3 (1 sec later)
 *
 * @example
 * const leading = debounce((value: number) => console.log(value), 1000, true);
 * leading(1); // output: 1
 * leading(2); // outputs nothing
 * leading(3); // output: 3 (1 sec later)
 */
export const debounce = <T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
  leading = false,
) => {
  let timeout: number | undefined;
  return (...args: Parameters<T>) => {
    const callNow = leading && !timeout;
    const later = () => {
      timeout = undefined;
      if (!callNow) {
        fn(...args);
      }
    };
    clearTimeout(timeout);
    timeout = window.setTimeout(later, delay);
    if (callNow) {
      fn(...args);
    }
  };
};

/**
 * Returns a new throttled function that will only execute the original function at most once per
 * the specified delay.
 * @param fn The function to throttle
 * @param delay The delay time in milliseconds
 * @returns A new throttled function
 */
export const throttle = <T extends (...args: any[]) => void>(fn: T, delay: number) => {
  let timeout: Timeout | undefined;
  return (...args: Parameters<T>) => {
    const callNow = !timeout;
    const later = () => {
      timeout = undefined;
      if (!callNow) {
        fn(...args);
      }
    };
    if (!timeout) {
      timeout = setTimeout(later, delay);
    }
    if (callNow) {
      fn(...args);
    }
  };
};

/**
 * Combines n functions. It’s a pipe flowing left-to-right,
 * calling each function with the output of the last one.
 * @param arg argument to apply to the first function
 * @param funcs function you need to apply to array
 * @deprecated if we really need this we should start using Ramda
 * @returns new array
 * @example
 * const applyXYZ = pipe([1,2,3], [x,y,z]) where x,y and z are functions
 */
export const pipe = <T>(arg: T, funcs: Array<(arg: T) => T>) =>
  funcs.reduce((acc, fn) => fn(acc), arg);
